import { useState, useEffect } from 'react';
import {
  Typography, Button, Table, Space, Tag, Modal, Form, Input, InputNumber, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import {
  getGoogleAdsCampaigns, createGoogleAdsCampaign, GoogleAdsCampaign,
} from 'services/google-ads.service';

const { Title, Text } = Typography;

const MICROS_PER_UAH = 1_000_000;
const uahFromMicros = (micros: number) => (micros / MICROS_PER_UAH).toFixed(2);

const DEFAULT_TIERS = [
  { label: '0-300', cpcBidUah: 1 },
  { label: '300-600', cpcBidUah: 2 },
  { label: '600-900', cpcBidUah: 3 },
  { label: '900+', cpcBidUah: 4 },
];

const GoogleAdsPage = () => {
  const [campaigns, setCampaigns] = useState<GoogleAdsCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const loadCampaigns = () => {
    setIsLoading(true);
    getGoogleAdsCampaigns()
      .then((r) => setCampaigns(r.data || []))
      .catch((e) => message.error(e?.message || 'Не вдалося завантажити кампанії'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      await createGoogleAdsCampaign({
        name: values.name,
        dailyBudgetUah: values.dailyBudgetUah,
        priceTiers: values.priceTiers,
      });
      message.success('Кампанію створено');
      form.resetFields();
      setModalOpen(false);
      loadCampaigns();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Помилка створення кампанії');
      loadCampaigns();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
        <Title level={2} style={{ margin: 0 }}>Google Ads кампанії</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Створити кампанію
        </Button>
      </Space>

      <Table<GoogleAdsCampaign>
        rowKey='_id'
        loading={isLoading}
        dataSource={campaigns}
        pagination={false}
        columns={[
          { title: 'Назва', dataIndex: 'name' },
          {
            title: 'Бюджет / день',
            dataIndex: 'budgetMicros',
            render: (v: number) => `${uahFromMicros(v)} грн`,
          },
          {
            title: 'Статус',
            dataIndex: 'status',
            render: (status: GoogleAdsCampaign['status'], record) => (
              <Space direction='vertical' size={0}>
                <Tag color={status === 'created' ? 'green' : 'red'}>{status}</Tag>
                {record.error && <Text type='danger' style={{ fontSize: 12 }}>{record.error}</Text>}
              </Space>
            ),
          },
          {
            title: 'Цінові групи',
            dataIndex: 'priceTiers',
            render: (tiers: GoogleAdsCampaign['priceTiers']) => (
              <Space direction='vertical' size={0}>
                {tiers.map((t) => (
                  <Text key={t.label} style={{ fontSize: 12 }}>
                    {t.label}
                    {': '}
                    {uahFromMicros(t.cpcBidMicros)}
                    {' грн/клік'}
                  </Text>
                ))}
              </Space>
            ),
          },
          {
            title: 'Створено',
            dataIndex: 'createdAt',
            render: (v: string) => new Date(v).toLocaleString(),
          },
        ]}
      />

      <Modal
        title='Створити Google Ads кампанію'
        visible={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={creating}
        okText='Створити'
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout='vertical'
          initialValues={{ priceTiers: DEFAULT_TIERS }}
        >
          <Form.Item name='name' label='Назва кампанії' rules={[{ required: true, message: 'Вкажіть назву' }]}>
            <Input placeholder='Наприклад: Shopping — Price Tiers' />
          </Form.Item>

          <Form.Item name='dailyBudgetUah' label='Денний бюджет (грн)' rules={[{ required: true, message: 'Вкажіть бюджет' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.List name='priceTiers'>
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align='baseline' style={{ display: 'flex' }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'label']}
                      label={key === 0 ? 'Ціновий діапазон' : undefined}
                    >
                      <Input disabled />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'cpcBidUah']}
                      label={key === 0 ? 'Ставка за клік (грн)' : undefined}
                      rules={[{ required: true, message: 'Вкажіть ставку' }]}
                    >
                      <InputNumber min={0.01} step={0.5} />
                    </Form.Item>
                  </Space>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default GoogleAdsPage;
